import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi } from '@/services/api';
import { Tag, Plus, Edit2, Loader2, AlertCircle } from 'lucide-react';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';

interface Model {
  name: string;
  model_name: string;
  description: string;
}

export default function ModelsTab() {
  const qc = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Model | null>(null);

  const [form, setForm] = useState({
    model_name: '',
    description: '',
  });

  const [errorMsg, setErrorMsg] = useState('');
  useEscapeToClose(() => setIsModalOpen(false), isModalOpen);

  const { data: models = [], isLoading } = useQuery({
    queryKey: ['modelsList'],
    queryFn: async () => {
      const res = await posApi.getModels();
      return res.data.message as Model[];
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => posApi.createModel(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['modelsList'] });
      qc.invalidateQueries({ queryKey: ['posModels'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || 'Failed to create model');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => posApi.updateModel(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['modelsList'] });
      qc.invalidateQueries({ queryKey: ['posModels'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || 'Failed to update model');
    },
  });

  const resetForm = () => {
    setForm({ model_name: '', description: '' });
    setEditing(null);
    setErrorMsg('');
  };

  const handleEdit = (model: Model) => {
    setEditing(model);
    setForm({
      model_name: model.model_name,
      description: model.description || '',
    });
    setErrorMsg('');
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.model_name.trim()) {
      setErrorMsg('Model name is required');
      return;
    }
    
    if (editing) {
      updateMutation.mutate({ ...form, name: editing.name });
    } else {
      createMutation.mutate(form);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Models</h3>
          <p className="text-sm text-gray-500">Manage item models (without delete function)</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Model
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 text-gray-900 font-medium border-b border-gray-200">
              <tr>
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Description</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {models.map((model) => (
                <tr key={model.name} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{model.model_name}</td>
                  <td className="px-6 py-4 max-w-xs truncate">{model.description || '-'}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end">
                      <button
                        onClick={() => handleEdit(model)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit Model"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {models.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-6 py-8 text-center text-gray-500">
                    No models found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <Tag className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {editing ? 'Edit Model' : 'Add Model'}
                </h3>
                <p className="text-sm text-gray-500">
                  {editing ? 'Update model details' : 'Create a new model'}
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {errorMsg && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <p>{errorMsg}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model Name *</label>
                <input
                  type="text"
                  value={form.model_name}
                  onChange={(e) => setForm({ ...form, model_name: e.target.value })}
                  className="input-field w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input-field w-full"
                  rows={3}
                />
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 btn-primary flex justify-center items-center gap-2"
                >
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
